import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from "prop-types";
import { DatePicker, Input, Modal, Radio, Select, Tag } from 'antd';
import { saveRepresentant, updateRepresentant } from '../../../../../hooks/useHooks';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import axiosInstance from '../../../../../variables/api/axiosInstance';
const { confirm } = Modal;

const TYPE_PERSONNE = {
    PHYSIQUE: 'PHYSIQUE',
    MORALE: 'MORALE',
};

const AddRepresentant = (props) => {
    const [representant, setRepresentant] = useState(props?.representant);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [professions, setProfessions] = useState([]);
    const [typePersonne, setTypePersonne] = useState(
        props?.representant?.typePersonne || TYPE_PERSONNE.PHYSIQUE
    );

    const dragDataRef = useRef({ startX: 0, startY: 0, dragging: false });
    const modalContainerRef = useRef(null);

    // ── Drag logic ────────────────────────────────────────────────────────────

    const handleMouseMove = useCallback((event) => {
        if (!dragDataRef.current.dragging) return;
        setModalPosition({
            x: event.clientX - dragDataRef.current.startX,
            y: event.clientY - dragDataRef.current.startY,
        });
    }, []);

    const handleMouseUp = useCallback(() => {
        if (!dragDataRef.current.dragging) return;
        dragDataRef.current.dragging = false;
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove]);

    const handleTouchMove = useCallback((event) => {
        if (!dragDataRef.current.dragging || event.touches.length !== 1) return;
        const touch = event.touches[0];
        setModalPosition({
            x: touch.clientX - dragDataRef.current.startX,
            y: touch.clientY - dragDataRef.current.startY,
        });
        event.preventDefault();
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (!dragDataRef.current.dragging) return;
        dragDataRef.current.dragging = false;
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
        window.removeEventListener('touchcancel', handleTouchEnd);
    }, [handleTouchMove]);

    const handleMouseDown = useCallback((event) => {
        if (event.button !== 0) return;
        const headerNode = modalContainerRef.current?.querySelector('.ant-modal-header');
        const closeNode = modalContainerRef.current?.querySelector('.ant-modal-close');
        if (!headerNode || !headerNode.contains(event.target) || (closeNode && closeNode.contains(event.target))) return;
        dragDataRef.current = {
            startX: event.clientX - modalPosition.x,
            startY: event.clientY - modalPosition.y,
            dragging: true,
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        event.preventDefault();
    }, [modalPosition, handleMouseMove, handleMouseUp]);

    const handleTouchStart = useCallback((event) => {
        if (event.touches.length !== 1) return;
        const headerNode = modalContainerRef.current?.querySelector('.ant-modal-header');
        const closeNode = modalContainerRef.current?.querySelector('.ant-modal-close');
        if (!headerNode || !headerNode.contains(event.target) || (closeNode && closeNode.contains(event.target))) return;
        const touch = event.touches[0];
        dragDataRef.current = {
            startX: touch.clientX - modalPosition.x,
            startY: touch.clientY - modalPosition.y,
            dragging: true,
        };
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd);
        window.addEventListener('touchcancel', handleTouchEnd);
        event.preventDefault();
    }, [modalPosition, handleTouchMove, handleTouchEnd]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    /** Changement du type de personne : réinitialise le formulaire */
    const handleTypePersonneChange = (e) => {
        const newType = e.target.value;
        setTypePersonne(newType);
        setRepresentant({ typePersonne: newType });
    };

    const handleChange = (e, nom) => {
        if (nom === 'profession' || nom === 'sexe' || nom === 'typePersonneMorale') {
            setRepresentant((prev) => ({ ...prev, [nom]: e }));
        } else if (nom) {
            setRepresentant((prev) => ({ ...prev, [nom]: e }));
        } else {
            const { name, value } = e.target;
            setRepresentant((prev) => ({ ...prev, [name]: value.toUpperCase() }));
        }
    };

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        setIsModalVisible(props.open);
        if (props.open) {
            const rep = props.representant;
            const detectedType = rep?.typePersonne || TYPE_PERSONNE.PHYSIQUE;
            setTypePersonne(detectedType);

            if (rep) {
                setRepresentant({
                    ...rep,
                    typePersonne: detectedType,
                    sexe: rep?.genre || rep?.sexe,
                    dateNaissance: rep?.dateNaissance ? dayjs(rep.dateNaissance) : null,
                });
            } else {
                setRepresentant({ typePersonne: detectedType });
            }

            setModalPosition({ x: 0, y: 0 });

            axiosInstance.get(`/api/parametre/type/PROFESSION`)
                .then((res) => setProfessions(res.data))
                .catch((err) => console.error(err));
        } else {
            dragDataRef.current.dragging = false;
        }
    }, [props.open]);

    useEffect(() => {
        if (!isModalVisible) props.setOpen(false);
    }, [isModalVisible]);

    useEffect(() => {
        const headerNode = modalContainerRef.current?.querySelector('.ant-modal-header');
        if (headerNode) {
            headerNode.style.cursor = 'move';
            headerNode.style.userSelect = 'none';
            headerNode.style.touchAction = 'none';
        }
    }, [isModalVisible]);

    useEffect(() => () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
        window.removeEventListener('touchcancel', handleTouchEnd);
    }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    // ── Save ──────────────────────────────────────────────────────────────────

    const handleSave = async () => {
        try {
            const payload = { ...representant, typePersonne };

            if (payload.dateNaissance && typePersonne === TYPE_PERSONNE.PHYSIQUE) {
                payload.dateNaissance = payload.dateNaissance.format('YYYY-MM-DD');
            }

            payload.victime = props.victime?.codeId;
            setUpdateLoading(true);

            if (props.edit) {
                await updateRepresentant(payload);
            } else {
                await saveRepresentant(payload);
            }

            setIsModalVisible(false);
            toast.success('Représentant enregistré avec succès!', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            window.location.reload();
        } catch (error) {
            console.error('Erreur lors de la sauvegarde du représentant : ', error);
            toast.error('Une erreur est survenue', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleClose = () => setIsModalVisible(false);

    // ── Sous-formulaires ──────────────────────────────────────────────────────

    const renderPersonnePhysique = () => (
        <>
            <div className="col-4">
                <label htmlFor="nom" style={labelStyle}>Nom du représentant *</label>
                <Input
                    placeholder="Nom du représentant"
                    name="nom"
                    value={representant?.nom || ""}
                    onChange={handleChange}
                />
            </div>
            <div className="col-4">
                <label htmlFor="prenom" style={labelStyle}>Prénoms du représentant *</label>
                <Input
                    placeholder="Prénoms du représentant"
                    name="prenom"
                    value={representant?.prenom || ""}
                    onChange={handleChange}
                />
            </div>
            <div className="col-4">
                <label htmlFor="sexe" style={{ ...labelStyle, width: "100%" }}>Genre du représentant *</label>
                <Select
                    placeholder="Sexe du représentant"
                    value={representant?.sexe || undefined}
                    onChange={(e) => handleChange(e, "sexe")}
                    options={[
                        { value: "MASCULIN", label: "MASCULIN" },
                        { value: "FEMININ", label: "FEMININ" },
                    ]}
                    style={{ width: "100%" }}
                />
            </div>
            <div className="col-4">
                <label htmlFor="dateNaissance" style={labelStyle}>Date de naissance *</label>
                <DatePicker
                    name="dateNaissance"
                    value={representant?.dateNaissance || null}
                    onChange={(e) => handleChange(e, "dateNaissance")}
                    style={{ width: "100%" }}
                    disabledDate={(current) => current && current > dayjs().endOf('day')}
                />
            </div>
            <div className="col-4">
                <label htmlFor="adresse" style={labelStyle}>Adresse du représentant *</label>
                <Input
                    placeholder="Adresse du représentant"
                    name="adresse"
                    value={representant?.adresse || ""}
                    onChange={handleChange}
                />
            </div>
            <div className="col-4">
                <label htmlFor="profession" style={{ ...labelStyle, width: "100%" }}>Profession du représentant *</label>
                <Select
                    showSearch
                    placeholder="Profession du représentant"
                    value={representant?.profession || undefined}
                    onChange={(e) => handleChange(e, "profession")}
                    options={professions.map((p) => ({
                        label: p.libelleParametre,
                        value: p.libelleParametre,
                    }))}
                    style={{ width: "100%" }}
                />
            </div>
            <div className="col-4">
                <label htmlFor="telephone" style={labelStyle}>Numéro de téléphone *</label>
                <Input
                    placeholder="Numéro de téléphone"
                    name="telephone"
                    value={representant?.telephone || ""}
                    onChange={handleChange}
                />
            </div>
        </>
    );

    const renderPersonneMorale = () => (
        <>
            <div className="col-4">
                <label htmlFor="raisonSociale" style={labelStyle}>Dénomination sociale *</label>
                <Input
                    placeholder="Dénomination sociale"
                    name="nom"
                    value={representant?.nom || ""}
                    onChange={handleChange}
                />
            </div>
            <div className="col-4">
                <label htmlFor="typePersonneMorale" style={{ ...labelStyle, width: "100%" }}>Type de structure *</label>
                <Select
                    placeholder="Type de structure"
                    value={representant?.fonction || undefined}
                    onChange={(e) => handleChange(e, "fonction")}
                    options={[
                        { value: "SOCIETE", label: "Société" },
                        { value: "CABINET", label: "Cabinet" },
                        // { value: "ASSOCIATION", label: "Association" },
                        // { value: "ONG", label: "ONG" },
                        { value: "AUTRE", label: "Autre" },
                    ]}
                    style={{ width: "100%" }}
                />
            </div>
            <div className="col-4">
                <label htmlFor="numeroRccm" style={labelStyle}>N° RCCM / Immatriculation</label>
                <Input
                    placeholder="N° RCCM ou immatriculation"
                    name="numeroRegistreCommerce"
                    value={representant?.numeroRegistreCommerce || ""}
                    onChange={handleChange}
                />
            </div>
            <div className="col-4">
                <label htmlFor="adresse" style={labelStyle}>Adresse du siège *</label>
                <Input
                    placeholder="Adresse du siège"
                    name="adresse"
                    value={representant?.adresse || ""}
                    onChange={handleChange}
                />
            </div>
            <div className="col-4">
                <label htmlFor="telephone" style={labelStyle}>Numéro de téléphone *</label>
                <Input
                    placeholder="Numéro de téléphone"
                    name="telephone"
                    value={representant?.telephone || ""}
                    onChange={handleChange}
                />
            </div>
            <div className="col-4">
                <label htmlFor="email" style={labelStyle}>Adresse e-mail</label>
                <Input
                    placeholder="Adresse e-mail"
                    name="email"
                    type="email"
                    value={representant?.email || ""}
                    onChange={handleChange}
                />
            </div>
            {/* Personne de contact au sein de la structure */}
            <div className="col-12">
                <div style={{
                    margin: "12px 0 6px",
                    fontWeight: 600,
                    color: '#242166',
                    borderBottom: '1px solid #e8e8e8',
                    paddingBottom: 4,
                }}>
                    Personne de contact
                </div>
            </div>
            <div className="col-6">
                <label htmlFor="contactNom" style={labelStyle}>Nom complet du contact *</label>
                <Input
                    placeholder="Nom complet du contact"
                    name="prenom"
                    value={representant?.prenom || ""}
                    onChange={handleChange}
                />
            </div>
            <div className="col-6">
                <label htmlFor="contactFonction" style={labelStyle}>Fonction du contact</label>
                <Input
                    placeholder="Ex : Directeur, Gérant…"
                    name="profession"
                    value={representant?.profession || ""}
                    onChange={handleChange}
                />
            </div>
        </>
    );

    // ── Shared label style ────────────────────────────────────────────────────

    const labelStyle = { margin: "10px 0 10px 0", color: null };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Modal
            title={props.edit ? "Modifier le représentant" : "Enregistrer un représentant"}
            open={isModalVisible}
            confirmLoading={updateLoading}
            loading={updateLoading}
            modalRender={(modalNode) => (
                <div
                    ref={modalContainerRef}
                    className="draggable-ayant-droit-modal"
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    style={{
                        transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
                        transition: 'none',
                    }}
                >
                    {modalNode}
                </div>
            )}
            onOk={handleSave}
            onCancel={handleClose}
            okText="Sauvegarder"
            cancelText="Annuler"
            width="1000px"
            zIndex={3000}
            okButtonProps={{ style: { background: '#242166' } }}
        >
            {/* En-tête victime */}
            <div style={{ marginBottom: 12 }}>
                Représentant{props.victime?.decesSinistre ? " des ayants droit" : ""} de la victime :{" "}
                <Tag color="blue">
                    {props.victime?.personne?.nom} {props.victime?.personne?.prenom}
                </Tag>
            </div>

            {/* Sélection du type de personne */}
            <div style={{
                marginBottom: 16,
                padding: '10px 14px',
                background: '#f5f5f5',
                borderRadius: 6,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
            }}>
                <span style={{ fontWeight: 600, color: '#242166' }}>Type de représentant :</span>
                <Radio.Group
                    value={typePersonne}
                    onChange={handleTypePersonneChange}
                    // Ne pas autoriser le changement de type en mode édition
                    // si le type est déjà défini côté serveur
                    disabled={props.edit && !!props.representant?.typePersonne}
                >
                    <Radio value={TYPE_PERSONNE.PHYSIQUE}>Personne physique</Radio>
                    <Radio value={TYPE_PERSONNE.MORALE}>Personne morale (Société / Cabinet)</Radio>
                </Radio.Group>
            </div>

            {/* Champs dynamiques selon le type */}
            <div className="row">
                {typePersonne === TYPE_PERSONNE.PHYSIQUE
                    ? renderPersonnePhysique()
                    : renderPersonneMorale()
                }
            </div>
        </Modal>
    );
};

AddRepresentant.propTypes = {
    open: PropTypes.bool,
    representant: PropTypes.object,
    victime: PropTypes.object,
    edit: PropTypes.bool,
    setOpen: PropTypes.func,
};

export default AddRepresentant;